import { useCallback, useState, useEffect } from 'react';
import { useMutation } from 'react-apollo';
import { useDispatch } from 'react-redux';

import { accountCardClone as accountCardCloneMutation } from '../../../../services/cards/graphql/mutations/accountCard';
import { openAlert } from '../../../../actions/modal-actions';
import { getErrorString } from '../../../../util/normalize-errors';
import { sentryCaptureMessage } from '../../../../util/sentry';

/**
 * AccountCard cloning hook.
 *
 * @param {Object} options The options.
 * @param {function} [options.onSuccess] Success callback.
 * @returns {{onAccountCardClone: (function(*): Promise<undefined>), cloning: boolean}}
 */
const useAccountCardClone = ({ onSuccess }) => {
  const [cloning, setCloning] = useState(false);
  const [clonedAccountCard, setClonedAccountCard] = useState();
  const [accountCardClone] = useMutation(accountCardCloneMutation);
  const dispatch = useDispatch();
  const onErrorModalOpen = useCallback(
    state => dispatch(openAlert(state)),
    [dispatch],
  );

  useEffect(() => {
    if (clonedAccountCard) {
      onSuccess?.(clonedAccountCard);
    }
  }, [clonedAccountCard]);

  const onAccountCardClone = async accountCard => {
    if (cloning) {
      return;
    }

    setCloning(true);
    setClonedAccountCard();

    try {
      const response = await accountCardClone({
        variables: {
          input: {
            account_card_id: accountCard.id,
            clone_element_configurations: true,
            clone_recipients: true,
          },
        },
      });

      setClonedAccountCard(response.data.accountCardClone.account_card);
    } catch (responseError) {
      onErrorModalOpen({
        message:
          'There was an error cloning your card, please try again later.',
        info: responseError?.request_id || getErrorString(responseError),
      });

      sentryCaptureMessage(
        responseError?.message ??
          '[useAccountCardClone] :: Error cloning AccountCard',
        {
          level: 'error',
          error: responseError,
        },
      );
    } finally {
      setCloning(false);
    }
  };

  return {
    cloning,
    onAccountCardClone,
  };
};

export default useAccountCardClone;
